<template>
  <div class="my-8 px-6" v-if="data?.sponsorship">
    <div class="relative">
      <div
        class="md:inline-block pl-4 pr-20 md:pl-20 md:pr-10 py-1 bg-blue-800 rounded-t-4 text-white"
      >
        <span class="text-14 font-semibold uppercase">{{ data.sponsorship.title }}</span>
      </div>
      <div
        class="grid md:grid-cols-7 box-container overflow-visible p-2 border-b-4 border-transparent rounded-t-none md:rounded-tr-16 lg:rounded-tr-20"
      >
        <div class="flex md:flex-col-reverse md:col-span-2 md:justify-end md:pb-4">
          <div class="w-full p-2 md:py-0">
            <BackgroundImage
              class="h-16"
              image-class="object-left md:object-center"
              size="contain"
              :src="data.sponsorship.psp.logo.sm"
              :alt="data.sponsorship.psp.displayName"
            />
          </div>

          <div class="flex">
            <ScoreCircle
              v-if="data.sponsorship.psp.score"
              :score="data.sponsorship.psp.score.value"
              size="sm"
              class="relative shrink-0 mr-2 -mt-8 md:-ml-4"
            />
            <span class="hidden md:block text-12 lg:text-14 mt-4">
              {{ $t('monito_score') }}
            </span>
          </div>
        </div>

        <div class="p-4 md:px-6 md:col-span-5">
          <div class="typography no-top-spacing mb-3 font-semibold">
            {{ data.sponsorship.description }}
          </div>
          <a
            class="btn btn-sm btn-amber"
            :href="addAffiliateMobileAppParams(data.sponsorship.primaryCta.link)"
            :target="getTarget(data.sponsorship.primaryCta.link)"
            :rel="getRel(data.sponsorship.primaryCta.link)"
          >
            {{ data.sponsorship.primaryCta.label }}
          </a>
        </div>
      </div>

      <div
        class="absolute inset-0 pointer-events-none overflow-hidden rounded-12 md:rounded-16 lg:rounded-20"
      >
        <div class="absolute bottom-0 w-full h-1 bg-blue-800"></div>
      </div>
    </div>
    <div v-if="data.sponsorship.secondaryCta" class="mt-3 text-center">
      <a
        class="link"
        :href="addAffiliateMobileAppParams(data.sponsorship.secondaryCta.link)"
        :target="getTarget(data.sponsorship.secondaryCta.link)"
        :rel="getRel(data.sponsorship.secondaryCta.link)"
        >{{ data.sponsorship.secondaryCta.label }}</a
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  GQLProviderSponsorship,
  GQLProviderSponsorshipVariables,
} from './generated/provider-sponsorship'
import QUERY_PROVIDER_SPONSORSHIP from './provider-sponsorship.graphql'

const props = defineProps<{
  currencyFrom: { iso: string }
  currencyTo: { iso: string }
}>()

const { $apollo } = useNuxtApp()
const logger = useLogger('sponsorship-card')

const { data } = await useAsyncData('hero-exchange-rates-sponsorship', async () => {
  try {
    const variables = {
      url: getCurrencyUrl({ currencyFrom: props.currencyFrom, currencyTo: props.currencyTo }),
      // entries in DB are registered with the old name
      slice: 'hero_exchange_rate_alert',
    }
    const { data, errors } = await $apollo.query<
      GQLProviderSponsorship,
      GQLProviderSponsorshipVariables
    >({
      query: QUERY_PROVIDER_SPONSORSHIP,
      variables,
    })
    if (errors) {
      logger.error({ errors }, 'Error while getting sponsorship')
      return { sponsorship: null }
    }
    const sponsorship = data.providerSponsorship?.sponsorship ?? null
    return { sponsorship }
  } catch (error) {
    logger.error(error)
  }
  return { sponsorship: null }
})

const { siteUrl } = useRuntimeConfig().public

function getTarget(url: string) {
  return url.startsWith(siteUrl) ? '_self' : '_blank'
}

function getRel(url: string) {
  return url.startsWith(siteUrl) ? '' : 'nofollow noopener'
}
</script>
